// ============================================================
// Datei: frontend/src/context/AuthContext.tsx
// Auth Context — Login, Register, Verify, Logout
// ============================================================
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, AuthUser } from '../services/api';

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, username: string, password: string) => Promise<string>;
  verify: (email: string, code: string) => Promise<void>;
  resendCode: (email: string) => Promise<string>;
  logout: () => void;
  updateUser: (user: AuthUser) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth muss in AuthProvider sein');
  return ctx;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem('auth_token');
      const storedUser = localStorage.getItem('auth_user');
      if (stored) {
        setToken(stored);
        try {
          const res = await authApi.me();
          setUser(res.data);
          localStorage.setItem('auth_user', JSON.stringify(res.data));
        } catch {
          if (storedUser) {
            try { setUser(JSON.parse(storedUser)); } catch { /* */ }
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
          }
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const setAuth = useCallback((t: string, u: AuthUser) => {
    localStorage.setItem('auth_token', t);
    localStorage.setItem('auth_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    setAuth(res.data.token, res.data.user);
  }, [setAuth]);

  // Register gibt jetzt nur Message zurück (kein Token)
  const register = useCallback(async (email: string, username: string, password: string): Promise<string> => {
    const res = await authApi.register(email, username, password);
    return res.data.message;
  }, []);

  // Verify gibt Token zurück
  const verify = useCallback(async (email: string, code: string) => {
    const res = await authApi.verify(email, code);
    setAuth(res.data.token, res.data.user);
  }, [setAuth]);

  const resendCode = useCallback(async (email: string): Promise<string> => {
    const res = await authApi.resend(email);
    return res.data.message;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((u: AuthUser) => {
    setUser(u);
    localStorage.setItem('auth_user', JSON.stringify(u));
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      isAuthenticated: !!token && !!user,
      login, register, verify, resendCode, logout, updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;